import { Placement } from '@floating-ui/dom';
import { format } from 'date-fns/format';
import { isEqual } from 'date-fns/isEqual';
import type { Locale } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import { parse } from 'date-fns/parse';
import { createFocusTrap, FocusTrap } from 'focus-trap';
import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksDatePickerChangeEvent } from '../../events';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import type { AutoPositionInterface, Position } from '../../util/common-modal';
import { AllowedPlacements } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import '../calendar/calendar';
import { BlocksCalendar } from '../calendar/calendar';
import { getTimezoneConfig } from '../calendar/date-utils';
import '../icon/icons/calendar';
import '../input-field/input-field';
import type { DateFieldPosition, DateFieldSizeOptions, DatePickerMessageType } from './datepicker-field.model';
import styles from './datepicker-field.styles';

/**
 * @slot field-note - The dropdown field note
 * @slot error - The dropdown error note
 * @slot warning - The dropdown warning note
 * @fires bl-date-picker-open - emitted when date-picker is opened
 * @fires bl-date-picker-close - emitted when date-picker is closed
 * @fires bl-date-picker-change - emitted when date is changed
 * @csspart base - The component's base wrapper.
 * @csspart loader-label - The component's loader-label wrapper.
 * @csspart label-skeleton - The component's label-skeleton wrapper.
 * @csspart body-loader - The component's body-loader.
 * @csspart body-skeleton - The component's body-skeleton.
 * @csspart icon-calendar - The component's calendar icon.
 * @csspart field-note-loader - The component's field-note-loader.
 * @csspart field-note-skeleton - The component's field-note-skeleton.
 * @csspart body - The component's body.
 * @csspart input - The component's `<cat-input-field>`.
 * @csspart after - The component's after slot.
 * @csspart popup-wrapper - The component's popup wrapper.
 * @csspart popup - The component's popup.
 * @csspart popup-body - The component's popup body.
 * @csspart calendar - The component's `<cat-calendar>`.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-error - The component's error slot.
 * @csspart part-warning - The component's warning slot.
 */
export class BlocksDatepickerField extends BlocksElement implements AutoPositionInterface {
  static formAssociated = true;static styles: CSSResultGroup = styles;
  /**
   * locale object from date-fns/locale
   */
  @property({ type: Object })
  locale: Locale = enUS;

  /**
   * The unique id of the select field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The field's label
   */
  @property({ type: String })
  label = 'Date';

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * The field's name attribute
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * The field's value attribute
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * The field's placeholder attribute
   */
  @property({ type: String })
  placeholder: string | undefined;

  /**
   * The dropdown field note
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * The dropdown error note
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * The dropdown warning note
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note in field to the input for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * The  field's required attribute
   */
  @property({ type: Boolean })
  required = false;

  /**
   * The select field's disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * Value to track datepicker display
   */
  @property({ type: Boolean })
  showDatepicker: boolean | undefined;

  /**
   * AutoFocus attribute
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Specify date format for UI display
   */
  @property({ type: String })
  dateFormat = 'MMM dd, yyyy';

  /**
   * Minimum date for disabled dates (format: yyyy/mm/dd)
   */
  @property({ type: String })
  disabledMinDate: string | undefined;

  /**
   * Maximum date for disabled dates (format: yyyy/mm/dd)
   */
  @property({ type: String })
  disabledMaxDate: string | undefined;

  /**
   * Array of specific dates to be disabled
   */
  @property({ type: Array })
  disabledDates: string[] | undefined;

  /**
   * Array of week days to be disabled
   * Format: '0' for Sunday, '1' for Monday, '2' for Tuesday, '3' for Wednesday, '4' for Thursday, '5' for Friday, '6' for Saturday
   * Example: ['0', '6'] to disable Sundays and Saturdays
   **/
  @property({ type: Array })
  disabledWeekDays: string[] | undefined;

  /**
   * Start date for disabling a range of dates
   */
  @property({ type: String })
  disabledRangeStartDate: string | undefined;

  /**
   * End date for disabling a range of dates
   */
  @property({ type: String })
  disabledRangeEndDate: string | undefined;

  /**
   * Amount of years to go before and after the current date
   */
  @property({ type: Number })
  multiYear = 3;

  @property({ type: String })
  previousButtonText = 'Previous Month';

  @property({ type: String })
  nextButtonText = 'Next Month';

  /**
   * Positions the dropdown panel absolutely to the datepicker.
   * If `isInline` is false, and if specified position doesn't fit inside the viewport,
   * it will try to accommodate in other positions which fits best.
   */
  @property({ type: String })
  position: DateFieldPosition = 'auto';

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** increases the datepicker height with label inside</li>
   * <li>**md** reduce the datepicker height with label outside </li>
   * <li>**sm** reduce  the datepicker height without label</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: DateFieldSizeOptions = 'lg';

  /**
   * Status of datepicker input field
   */
  @property({ type: String })
  status: DatePickerMessageType;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean })
  readonly: boolean | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * Aria label attribute
   * - Provides an accessible name for the datepicker element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * The `timeZone` property represents the time zone that will be passed to the `TZDate` object.
   * This property accepts a string that can either be:
   * - **An IANA time zone identifier** (e.g., `'America/New_York'`, `'Europe/London'`, `'Asia/Kolkata'`).
   * - **A UTC offset string** (e.g., `'-0800'` for UTC-8 hours, or `'+0530'` for UTC+5:30 hours).
   *
   * If not provided, the default value will be `undefined`, and the system's local time zone will be used.
   **/
  @property({ type: String })
  timeZone: string | undefined;

  /**
   * Query the date input field wrapper
   */
  @query('.cat-c-datepicker-field__body')
  private _catDateInputWrapper: HTMLElement | undefined;

  /**
   * Query the date calendar popup
   */
  @query('.cat-c-datepicker-field__popup-wrapper')
  private _catDateCalendarPopup: HTMLElement | undefined;

  /*
   *  Query the calendar
   */
  @query('cat-calendar')
  private _catCalendar: BlocksCalendar | undefined;

  @query('cat-input-field')
  private _catInput: HTMLInputElement | undefined;

  /*
   * Tab focus trap
   */
  private _focusTrap?: FocusTrap;

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  private isPopoverSupported = isPopoverSupported();

  private get isPopoverEnabled(): boolean {
    return this.isPopoverSupported && !this.isInline;
  }

  public getFloatingElement(): HTMLElement {
    return this._catDateCalendarPopup!;
  }
  public getPosition(): Position {
    return this.position;
  }
  public getIsInline(): boolean {
    return this.isInline;
  }
  public getReferenceElement(): HTMLElement {
    return this._catDateInputWrapper!;
  }
  public getOverflowElement(): HTMLElement | null {
    return this._catDateCalendarPopup!;
  }
  public getAllowedPlacements(): Placement[] {
    return AllowedPlacements;
  }

  private internals_ = this.attachInternals?.();
  protected autoPositionController = new AutoPositionController(this);

  protected slots = new SlotController(this, 'error', 'warning', 'field-note');

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }
  private get hasErrorNoteSlot(): boolean {
    return this.slots.hasSlotted('error');
  }
  private get hasWarningNoteSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }

  /**
   * Connected callback lifecycle
   * 1) Add event listeners
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    this.fieldId = this.fieldId || nanoid();
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    this._focusTrap?.deactivate();
  }

  /**
   * firstUpdated lifecycle
   * 1) Add keydown event listener to the input field
   */
  firstUpdated() {
    setTimeout(() => {
      // Find the input field inside the cat-input-field
      if (this.showDatepicker) {
        this.dynamicPosition();
      }
    }, 1);
  }

  protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    await this.updateComplete;
    if (_changedProperties.has('value') && this.value) {
      this.internals_?.setFormValue(this.value);
    }
    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
    /**To activate focus trap */
    if (this.showDatepicker && _changedProperties.has('showDatepicker')) {
      this.setFocusTrap();
      if (this.value) {
        setTimeout(() => {
          const element = this._catCalendar?.shadowRoot?.querySelector<HTMLButtonElement>('.is-active');
          if (element) {
            this._catCalendar?.setFocusOnDate(element);
          }
        }, 10);
      }
    }
  }

  /**
   * Forwards focus to the internal input element.
   */
  override focus(): void {
    this._catInput?.focus();
  }

  private setFocusTrap() {
    const calendarPanel = this._catCalendar;
    if (calendarPanel) {
      this._focusTrap = createFocusTrap(calendarPanel, {
        initialFocus: false,
        tabbableOptions: { getShadowRoot: true },
        fallbackFocus: calendarPanel,
        allowOutsideClick: true
      });

      /**To activate focus trap */
      this._focusTrap?.activate();
    }
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the calendar is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active calendar item
   * 5) If the calendar is active and we've clicked outside of the calendar then it should
   * be closed.
   */
  private handleOnClickOutside = (e: MouseEvent) => {
    /* 2 */
    if (!this.showDatepicker) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine navigation context during click handler');
    }

    /* 4 */
    const didClickInside = e.composedPath().includes(this.shadowRoot.host);

    /* 5 */
    if (this.showDatepicker && !didClickInside) {
      this.showDatepicker = false;
      this.hidePopup();
      this.focusOnInput();
    }
  };

  /**
   * Focus on input
   */
  private focusOnInput = () => {
    const inputField = this._catInput?.shadowRoot?.querySelector('input');
    if (inputField) {
      inputField.focus();
    }
  };

  /*
   * Handle click outside the component
   * 1) If user has already selected a value, navigates to other month/year, closes the panel and opens again.
    Then panel should display the grid that will have selected value
   * 2) If user has not selected any value and there is disabledMinDate and max Date specified,  navigates to other month/year within the range, closes the panel and opens again.
    Then panel should display the grid that will have the min Date
   * 3) If user has not selected any value,  navigates to other month/year, closes the panel and opens again.
    Then panel should display the grid that will have the current date
   */

  private updateCalendarGrid() {
    /* 1 */

    if (this.value && !isEqual(this._catCalendar?.setActiveDate ?? '', this._catCalendar?.navDate ?? '')) {
      this._catCalendar?.updateGrid(parse(this.value, this.dateFormat, new Date(), getTimezoneConfig(this.timeZone)));
    } else if (!this.value && !isEqual(this._catCalendar?.currentDate ?? '', this._catCalendar?.navDate ?? '')) {
      /*2 */
      if (this.disabledMaxDate && this.disabledMinDate) {
        const minDate = parse(this.disabledMinDate, this.dateFormat, new Date(), getTimezoneConfig(this.timeZone));
        this._catCalendar?.updateGrid(minDate);
      } else {
        /*3*/
        this._catCalendar?.updateGrid(this._catCalendar.currentDate);
      }
    }
  }

  /**
   * Handle calendar placement
   */
  private dynamicPosition() {
    setTimeout(() => {
      if (this._catDateCalendarPopup) {
        this.autoPositionController.openOverlay();
      }
    }, 1);
  }

  /**
   * Hide calender popup
   */
  private hidePopup() {
    this._catCalendar?.hidePopup();

    this.autoPositionController.closeOverlay();
    this.updateCalendarGrid();
    this.emitPanelCloseEvent();
    this._focusTrap?.deactivate();
  }
  /**
   * Handle keydown - Close the calendar popup when escape is hit
   */
  private handleCloseKeyDown = (e: KeyboardEvent) => {
    if (e.key === KEYS.ESCAPE) {
      this.showDatepicker = false;
      this.hidePopup();
      this.shadowRoot?.querySelector('cat-input-field')?.focus();
    }
  };

  /**
   * Show datepicker when clicked in the input field
   */
  private toggleActive() {
    if (this.disabled) {
      return;
    }
    this.showDatepicker = !this.showDatepicker;

    if (this.showDatepicker) {
      this.dynamicPosition();
      this.emitPanelOpenEvent();
    } else {
      this.hidePopup();
    }
  }

  /**
   * Emit open event when panel is opened
   */
  private emitPanelOpenEvent() {
    this.dispatch({ eventName: 'bl-date-picker-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
  }

  /**
   * Emit close event when panel is closed
   */
  private emitPanelCloseEvent() {
    this.dispatch({ eventName: 'bl-date-picker-close', detailObj: { isActive: false } }) as BlocksCloseEvent;
  }

  private formatDate(value: string | undefined): string | undefined {
    return value ? format(parse(value, this.dateFormat, new Date(), { locale: enUS }), this.dateFormat, { locale: this.locale }) : value;
  }

  /**
   * When calendar date is changed
   */
  private dateUpdate(e: CustomEvent) {
    this.value = e.detail.value || this.value;
    this.showDatepicker = false;
    this.hidePopup();
    this.dispatch({
      eventName: 'bl-date-picker-change',
      detailObj: {
        date: this.value,
        value: this.timeZone ? e.detail.rawDate : (e.detail.rawDate as Date).toISOString()
      }
    }) as BlocksDatePickerChangeEvent;
    this.focusOnInput();
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === KEYS.ESCAPE) {
      this.handleCloseKeyDown(e);
    }

    if (e.key === KEYS.DOWN_ARROW || e.key === KEYS.UP_ARROW) {
      e.preventDefault();
      this.showDatepicker && this._catCalendar?.focusFirstItem(e);
    }
  };

  private hasFieldNote() {
    return this.fieldNote || this.hasFieldNoteSlot || this.errorNote || this.hasErrorNoteSlot || this.warningNote || this.hasWarningNoteSlot;
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  private handleDatePickerOnInputFieldKeyDown(event: KeyboardEvent): void {
    this.handleCloseKeyDown(event);
    if ((event.key === KEYS.ENTER || event.key === KEYS.SPACE) && !this.readonly) {
      event.preventDefault();
      if (this.showDatepicker) {
        this.showDatepicker = false;
        this.hidePopup();
      } else {
        this.showDatepicker = true;
        this.emitPanelOpenEvent();
        this.dynamicPosition();
      }
    }
  }

  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassNames}">
        ${(this.size === 'md' || this.size === 'lg') && !this.hideLabel
          ? html` <span part="loader-label" class="cat-c-datepicker__medium-label"
              ><cat-skeleton
                part="label-skeleton"
                exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                class="class3"
                size="sm"
                width="128px"
              ></cat-skeleton>
            </span>`
          : nothing}

        <div part="body-loader" class="cat-c-datepicker__input-loader">
          <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')}> </cat-skeleton>
          <cat-icon-calendar
            part="icon-calendar"
            exportparts=${this.getExportParts('calendar', 'icon')}
            class="cat-c-datepicker__icon-loader"
            slot="after"
          ></cat-icon-calendar>
        </div>

        ${this.size !== 'sm' && this.hasFieldNote()
          ? html`<div part="field-note-loader" class="cat-c-datepicker__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                size="sm"
                width="80px"
              ></cat-skeleton>
            </div>`
          : nothing}
      </div>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-datepicker-field', {
      'cat-c-datepicker-field--hidden-label': this.hideLabel,
      'cat-c-datepicker-field--disabled': this.disabled,
      'cat-c-datepicker-field--required': this.required,
      'cat-c-datepicker-field--is-active': this.showDatepicker,
      'cat-c-datepicker-field--readonly': this.readonly,
      'cat-is-warning': this.status === 'warning',
      'cat-is-error': this.status === 'error',
      'cat-c-datepicker-field--large': this.size === 'lg',
      'cat-c-datepicker-field--medium': this.size === 'md',
      'cat-c-datepicker-field--small': this.size === 'sm',
      'cat-c-input-field--loader': this.isLoading === true,
      'cat-c-datepicker-field--has-error': this.hasErrorNoteSlot || this.errorNote,
      'cat-c-datepicker-field--has-warning': this.hasWarningNoteSlot || this.warningNote,
      'cat-c-datepicker-field--has-field-note': this.hasFieldNoteSlot || this.fieldNote
    });

    this.setAriaDescribedBy();

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    }

    return html`
      <div part="base" class="${componentClassNames}" @keydown=${this.handleKeyDown}>
        <div part="body" class="cat-c-datepicker-field__body">
          <cat-input-field
            @keydown=${this.handleDatePickerOnInputFieldKeyDown}
            part="input"
            exportparts=${this.getExportParts('input-field')}
            class="cat-c-datepicker--input-field"
            label="${this.label}"
            ?hideLabel="${this.hideLabel}"
            id="${ifDefined(this.fieldId)}"
            name="${ifDefined(this.name)}"
            .value="${this.formatDate(this.value) ?? ''}"
            ?autoFocus="${this.autoFocus}"
            ?required="${this.required}"
            ?disabled="${this.disabled}"
            aria-describedby="${ifDefined(this.ariaDescribedBy)}"
            placeholder="${this.placeholder ?? this.dateFormat}"
            @click=${this.toggleActive}
            .size=${this.size}
            .status=${this.status}
            ?readonly=${true}
            .notation=${this.notation}
          >
            <cat-icon-calendar part="after" exportparts=${this.getExportParts('icon-calendar', 'icon')} slot="after"></cat-icon-calendar>
          </cat-input-field>
          <div part="popup-wrapper" popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)} class="cat-c-datepicker-field__popup-wrapper">
            <div part="popup" class="cat-c-datepicker-field__popup">
              <div
                part="popup-body"
                class="cat-c-datepicker-field__popup-body"
                role="dialog"
                aria-label=${ifDefined(this.arialabel)}
                aria-modal="true"
              >
                <cat-calendar
                  part="calendar"
                  exportparts=${this.getExportParts('calendar')}
                  .locale=${this.locale}
                  .disabledMinDate=${this.disabledMinDate}
                  .disabledMaxDate=${this.disabledMaxDate}
                  .disabledDates=${this.disabledDates}
                  .disabledWeekDays=${this.disabledWeekDays}
                  .disabledRangeStartDate=${this.disabledRangeStartDate}
                  .disabledRangeEndDate=${this.disabledRangeEndDate}
                  .multiYear=${this.multiYear}
                  .previousButtonText=${this.previousButtonText}
                  .nextButtonText=${this.nextButtonText}
                  .dateFormat=${this.dateFormat}
                  .setActiveDate=${this.value}
                  .timeZone=${ifDefined(this.timeZone)}
                  @bl-calendar-change=${this.dateUpdate}
                ></cat-calendar>
              </div>
            </div>
          </div>
        </div>

        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
          .status=${this.status}
          ?isDisabled=${this.disabled}
          class="cat-c-datepicker-field__field-note cat-c-datepicker-field__field-note-slot"
        >
          <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
        </cat-field-note>
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          id=${ifDefined(this.errorNote || this.hasErrorNoteSlot ? this.ariaDescribedBy : undefined)}
          .status=${'error'}
          ?isDisabled=${this.disabled}
          class="cat-c-datepicker-field__field-note cat-c-datepicker-field__error-slot"
        >
          <slot part="part-error" name="error">${this.errorNote}</slot>
        </cat-field-note>
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          id=${ifDefined(this.warningNote || this.hasWarningNoteSlot ? this.ariaDescribedBy : undefined)}
          ?isDisabled=${this.disabled}
          class="cat-c-datepicker-field__field-note cat-c-datepicker-field__warning-slot"
        >
          <slot part="part-warning" name="warning">${this.warningNote}</slot>
        </cat-field-note>
      </div>
    `;
  }
}

if (customElements.get('cat-datepicker-field') === undefined) {
  customElements.define('cat-datepicker-field', BlocksDatepickerField);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-datepicker-field': BlocksDatepickerField;
  }
}
